import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { ArrowUpRight, Search } from 'lucide-react';

const MODES = ['cash', 'cheque', 'upi', 'neft', 'imps', 'other'];
const PARTY_TYPES = ['customer', 'vendor', 'other'];

export default function Receipt() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { currencySymbol } = useTenantFormat();

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    party_type: 'customer',
    party_id: null,
    party_name: '',
    to_account: 'cash',
    amount: '',
    mode: 'cash',
    reference_no: '',
    narration: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [partyQuery, setPartyQuery] = useState('');
  const [partyResults, setPartyResults] = useState([]);
  const [showPartyDrop, setShowPartyDrop] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.bankAccountsList(token).then(r => setAccounts(r.items || [])).catch(() => {});
  }, [token]);

  const searchParty = useCallback(async (q) => {
    if (!token || !q.trim()) { setPartyResults([]); return; }
    try {
      const r = await api.customersList(token, q);
      setPartyResults(r.items || r.customers || []);
    } catch { setPartyResults([]); }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => searchParty(partyQuery), 300);
    return () => clearTimeout(t);
  }, [partyQuery, searchParty]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Amount must be greater than zero'); return; }
    if (!form.to_account) { setError('Select a destination account'); return; }
    if (form.party_type !== 'other' && !form.party_id) { setError('Select a party'); return; }
    if (form.party_type === 'other' && !form.party_name.trim()) { setError('Enter a party name'); return; }
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        party_type: form.party_type,
        party_id: form.party_type !== 'other' ? form.party_id : null,
        party_name: form.party_type === 'other' ? form.party_name.trim() : null,
        to_account: form.to_account,
        amount: amt,
        mode: form.mode,
        reference_no: form.reference_no.trim() || null,
        narration: form.narration.trim() || null,
      };
      const r = await api.voucherReceiptCreate(token, payload);
      toast.show('Receipt voucher created', 'success');
      navigate(`/vouchers/${r.entry_id || r.id || ''}`);
    } catch (err) {
      setError(err?.message || 'Failed to create receipt');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="voucher-receipt-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
          <ArrowUpRight size={20} className="text-emerald-600" />
        </div>
        <div>
          <h1 className="text-gray-900 font-extrabold text-xl">Receipt Voucher</h1>
          <p className="text-gray-500 text-xs">Record an incoming receipt</p>
        </div>
      </div>

      <GlassCard className="p-6">
        <form onSubmit={submit} className="space-y-5">
          {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="voucher-error">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} data-testid="v-date"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
            </div>
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">To Account</label>
              <select value={form.to_account} onChange={e => set('to_account', e.target.value)} data-testid="v-to-account"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none">
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.account_number ? ` (${a.account_number})` : ''}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Party Type</label>
            <div className="flex gap-2" data-testid="v-party-type">
              {PARTY_TYPES.map(pt => (
                <button key={pt} type="button" onClick={() => { set('party_type', pt); set('party_id', null); set('party_name', ''); setPartyQuery(''); }}
                  className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all capitalize ${form.party_type === pt ? 'bg-brand-purple/10 border-brand-purple text-brand-purple' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
                  {pt}
                </button>
              ))}
            </div>
          </div>

          {form.party_type !== 'other' ? (
            <div className="relative">
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Party</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder={`Search ${form.party_type}...`} value={partyQuery}
                  onChange={e => { setPartyQuery(e.target.value); setShowPartyDrop(true); }}
                  onFocus={() => setShowPartyDrop(true)}
                  data-testid="v-party-search"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
              </div>
              {form.party_id && <p className="text-xs text-emerald-600 mt-1 font-semibold">Selected: {partyQuery}</p>}
              {showPartyDrop && partyResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-xl bg-white border border-gray-200 shadow-lg">
                  {partyResults.map(p => (
                    <button key={p.id} type="button" onClick={() => { set('party_id', p.id); setPartyQuery(p.name); setShowPartyDrop(false); }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-900 hover:bg-brand-purple/5 border-b border-gray-100 last:border-0">
                      {p.name} <span className="text-gray-400 text-xs">{p.phone || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Party Name</label>
              <input type="text" value={form.party_name} onChange={e => set('party_name', e.target.value)} placeholder="e.g. Cash deposit"
                data-testid="v-party-name"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Amount ({currencySymbol})</label>
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00"
                data-testid="v-amount"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm tabular-nums font-semibold focus:border-brand-purple outline-none" />
            </div>
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Mode</label>
              <div className="flex flex-wrap gap-1.5" data-testid="v-mode">
                {MODES.map(m => (
                  <button key={m} type="button" onClick={() => set('mode', m)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all capitalize ${form.mode === m ? 'bg-brand-purple/10 border-brand-purple text-brand-purple' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Reference No (optional)</label>
            <input type="text" value={form.reference_no} onChange={e => set('reference_no', e.target.value)} placeholder="Cheque no / UTR / Ref"
              data-testid="v-reference"
              className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Narration</label>
            <textarea value={form.narration} onChange={e => set('narration', e.target.value.slice(0, 200))} maxLength={200} rows={2} placeholder="Description..."
              data-testid="v-narration"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none resize-none" />
            <p className="text-gray-400 text-[10px] text-right mt-0.5">{form.narration.length}/200</p>
          </div>

          <button type="submit" disabled={submitting} data-testid="v-submit"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : 'Create Receipt Voucher'}
          </button>
        </form>
      </GlassCard>
    </div>
  );
}
